import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import { ChevronDown, Trash2, AlertTriangle, ShieldAlert, Loader } from 'lucide-react';

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const CategoriesTagsTab = ({ toast }) => {
  const { hasPermission } = useAuth();

  // Tab State: 'COMPANY' vs 'INDIVIDUAL'
  const [catalogTypeTab, setCatalogTypeTab] = useState('COMPANY');

  // Status Filter State
  const [categoryStatusFilter, setCategoryStatusFilter] = useState('ACTIVE'); // 'ACTIVE', 'INACTIVE'

  // Pagination State
  const [categoryPage, setCategoryPage] = useState(0);
  const [categoryPageSize, setCategoryPageSize] = useState(6);

  // Data States
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create Category Modal States
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', tags: '', brands: '' });

  // Edit Category Modal States
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Delete Category Confirmation Modal States (Double-Verification)
  const [deleteTargetCategory, setDeleteTargetCategory] = useState(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(1); // Step 1: Warning, Step 2: Confirm Name
  const [confirmCategoryNameInput, setConfirmCategoryNameInput] = useState('');
  const [deletingCategory, setDeletingCategory] = useState(false);

  const fetchCategories = useCallback(async () => {
    if (!hasPermission('MANAGE_CATALOG')) return;
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.getCategories(true);
      if (res.success) {
        setCategories(res.data || []);
      } else {
        setError(res.error || 'Failed to fetch categories.');
      }
    } catch (err) {
      setError('An error occurred while fetching categories.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCreateCategorySubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (catalogTypeTab === 'COMPANY') {
      const tagNames = categoryForm.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t !== '');

      if (tagNames.length === 0) {
        toast.warning("Please specify at least one search tag for Company Category.");
        return;
      }

      const brandNames = categoryForm.brands
        ? categoryForm.brands.split(',').map(b => b.trim()).filter(b => b !== '')
        : [];

      const payload = {
        name: categoryForm.name.trim(),
        description: categoryForm.description.trim(),
        categoryType: 'COMPANY',
        isActive: true,
        tagNames,
        brandNames
      };

      try {
        const res = await vendorService.createCategory(payload);
        if (res.success) {
          toast.success(`Company Category "${categoryForm.name}" created successfully.`);
          setCategoryForm({ name: '', description: '', tags: '', brands: '' });
          setShowCreateCategoryModal(false);
          fetchCategories();
        } else {
          toast.error("Failed to create category: " + res.error);
        }
      } catch (err) {
        toast.error("An error occurred while creating category.");
        console.error(err);
      }
    } else {
      // Individual Business Category Creation
      if (!categoryForm.name.trim()) {
        toast.warning("Please specify Individual Business Category Name.");
        return;
      }

      const payload = {
        name: categoryForm.name.trim(),
        description: categoryForm.description.trim(),
        categoryType: 'INDIVIDUAL',
        isActive: true
      };

      try {
        const res = await vendorService.createCategory(payload);
        if (res.success) {
          toast.success(`Individual Category "${categoryForm.name}" created successfully.`);
          setCategoryForm({ name: '', description: '', tags: '', brands: '' });
          setShowCreateCategoryModal(false);
          fetchCategories();
        } else {
          toast.error("Failed to create category: " + res.error);
        }
      } catch (err) {
        toast.error("An error occurred while creating category.");
        console.error(err);
      }
    }
  };

  const handleEditCategorySubmit = async (e) => {
    e.preventDefault();
    setError('');

    const catType = (selectedCategory.categoryType || selectedCategory.category_type || catalogTypeTab || 'COMPANY').toUpperCase();
    const isCompany = catType === 'COMPANY';

    let tagNames = [];
    let brandNames = [];

    if (isCompany) {
      tagNames = (selectedCategory.tagsString || '')
        .split(',')
        .map(t => t.trim())
        .filter(t => t !== '');

      if (tagNames.length === 0) {
        toast.warning("Please specify at least one search tag.");
        return;
      }

      brandNames = selectedCategory.brandsString
        ? selectedCategory.brandsString.split(',').map(b => b.trim()).filter(b => b !== '')
        : [];
    }

    const payload = {
      name: selectedCategory.name.trim(),
      description: (selectedCategory.description || '').trim(),
      categoryType: catType,
      isActive: selectedCategory.isActive
    };

    if (isCompany) {
      payload.tagNames = tagNames;
      payload.brandNames = brandNames;
    }

    try {
      const res = await vendorService.updateCategory(selectedCategory.id, payload);
      if (res.success) {
        toast.success(`Category "${selectedCategory.name}" updated successfully.`);
        setSelectedCategory(null);
        fetchCategories();
      } else {
        toast.error("Failed to update category: " + res.error);
      }
    } catch (err) {
      toast.error("An error occurred while updating category.");
      console.error(err);
    }
  };

  // Open Delete Double Verification Modal
  const initiateDeleteCategory = (category) => {
    setDeleteTargetCategory(category);
    setDeleteConfirmStep(1);
    setConfirmCategoryNameInput('');
  };

  const handleDeleteCategoryConfirmed = async () => {
    if (!deleteTargetCategory) return;
    setDeletingCategory(true);
    try {
      const res = await vendorService.deleteCategory(deleteTargetCategory.id);
      if (res.success) {
        toast.success(res.data?.message || `Category "${deleteTargetCategory.name}" deleted successfully.`);
        setDeleteTargetCategory(null);
        setDeleteConfirmStep(1);
        setConfirmCategoryNameInput('');
        fetchCategories();
      } else {
        toast.error(res.error || "Failed to delete category.");
      }
    } catch (err) {
      toast.error("An error occurred while deleting category.");
      console.error(err);
    } finally {
      setDeletingCategory(false);
    }
  };

  // Filter categories based on catalogTypeTab and statusFilter
  const typeFilteredCategories = categories.filter(c => {
    const cType = (c.categoryType || c.category_type || 'COMPANY').toUpperCase();
    return cType === catalogTypeTab;
  });

  const filteredCategories = typeFilteredCategories.filter(c => {
    if (categoryStatusFilter === 'ACTIVE') return c.isActive;
    if (categoryStatusFilter === 'INACTIVE') return !c.isActive;
    return true;
  });

  const totalCategoryPages = Math.ceil(filteredCategories.length / categoryPageSize);
  const safeCategoryPage = Math.min(categoryPage, Math.max(0, totalCategoryPages - 1));
  const displayedCategories = filteredCategories.slice(
    safeCategoryPage * categoryPageSize,
    (safeCategoryPage + 1) * categoryPageSize
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-150 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row gap-4 items-center justify-between select-none bg-white">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Categories & Catalog Directory</h2>
          <p className="text-xs text-gray-500 mt-1">Manage Company categories with tags/brands and Individual Business categories.</p>
        </div>
        <button
          onClick={() => {
            setCategoryForm({ name: '', description: '', tags: '', brands: '' });
            setShowCreateCategoryModal(true);
          }}
          className="bg-[#2ab391] hover:bg-[#23977a] text-white text-sm font-semibold py-2.5 px-4 rounded-lg shadow-md shadow-[#2ab391]/10 active:scale-[0.98] transition-all flex items-center gap-1.5 cursor-pointer border-0"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          {catalogTypeTab === 'COMPANY' ? 'Create Company Category' : 'Create Individual Category'}
        </button>
      </div>

      {/* Catalog Type Sub-Tabs */}
      <div className="bg-gray-100/70 p-1.5 border-b border-gray-200 flex gap-2">
        <button
          type="button"
          onClick={() => { setCatalogTypeTab('COMPANY'); setCategoryPage(0); }}
          className={`flex-1 py-2.5 px-4 text-xs font-bold rounded-xl transition-all cursor-pointer ${
            catalogTypeTab === 'COMPANY'
              ? 'bg-white text-gray-800 shadow-xs border border-gray-200/80'
              : 'text-gray-550 hover:text-gray-800 hover:bg-white/50'
          }`}
        >
          Company Categories
        </button>
        <button
          type="button"
          onClick={() => { setCatalogTypeTab('INDIVIDUAL'); setCategoryPage(0); }}
          className={`flex-1 py-2.5 px-4 text-xs font-bold rounded-xl transition-all cursor-pointer ${
            catalogTypeTab === 'INDIVIDUAL'
              ? 'bg-white text-[#2ab391] shadow-xs border border-teal-200/80'
              : 'text-gray-550 hover:text-gray-800 hover:bg-white/50'
          }`}
        >
          Individual Business Categories
        </button>
      </div>

      {/* Status Filters Header */}
      <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row gap-4 items-center justify-between bg-gray-50/50 select-none">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setCategoryStatusFilter('ACTIVE'); setCategoryPage(0); }}
            className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${categoryStatusFilter === 'ACTIVE'
              ? 'bg-[#2ab391] text-white border-transparent'
              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => { setCategoryStatusFilter('INACTIVE'); setCategoryPage(0); }}
            className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${categoryStatusFilter === 'INACTIVE'
              ? 'bg-[#2ab391] text-white border-transparent'
              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
          >
            Inactive
          </button>
        </div>

        <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">
          Total Categories: {filteredCategories.length}
        </span>
      </div>

      <div className="p-6">
        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full p-12 text-center text-gray-450 font-bold text-sm bg-white select-none border border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-2">
              <Loader className="w-5 h-5 animate-spin text-[#2ab391]" />
              <span>Loading categories...</span>
            </div>
          ) : error ? (
            <div className="col-span-full p-6 text-center text-rose-600 font-bold text-sm bg-rose-50 border border-rose-200 rounded-2xl">
              {error}
            </div>
          ) : displayedCategories.length === 0 ? (
            <div className="col-span-full p-12 text-center text-gray-450 font-bold text-sm bg-white select-none border border-dashed border-gray-200 rounded-2xl">
              No {catalogTypeTab === 'COMPANY' ? 'Company' : 'Individual Business'} categories found matching the filter.
            </div>
          ) : (
            displayedCategories.map(c => (
              <div key={c.id} className="border border-gray-150 rounded-2xl p-5 hover:shadow-lg transition-all duration-200 bg-white flex flex-col justify-between min-h-[180px] group">
                <div>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center border shadow-inner font-bold text-lg select-none ${
                        catalogTypeTab === 'INDIVIDUAL' 
                          ? 'bg-amber-50 text-amber-600 border-amber-200' 
                          : 'bg-teal-50 text-[#2ab391] border-teal-100/50'
                      }`}>
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800 text-base mb-1">{c.name}</h3>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border ${c.isActive
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                            : 'bg-gray-50 text-gray-500 border-gray-200'
                            }`}>
                            {c.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border ${
                            catalogTypeTab === 'INDIVIDUAL'
                              ? 'bg-purple-50 text-purple-700 border-purple-200'
                              : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}>
                            {catalogTypeTab === 'INDIVIDUAL' ? 'Individual' : 'Company'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setSelectedCategory({
                          id: c.id,
                          name: c.name,
                          description: c.description || '',
                          categoryType: c.categoryType || c.category_type || catalogTypeTab || 'COMPANY',
                          isActive: c.isActive,
                          tagsString: c.tags ? c.tags.map(t => t.name).join(', ') : '',
                          brandsString: c.brands ? c.brands.map(b => b.name).join(', ') : ''
                        })}
                        className="w-8 h-8 rounded-full bg-gray-50 hover:bg-teal-50 text-gray-400 hover:text-[#2ab391] flex items-center justify-center transition-colors border border-gray-100 hover:border-teal-100/50 cursor-pointer"
                        title="Edit Category"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => initiateDeleteCategory(c)}
                        className="w-8 h-8 rounded-full bg-gray-50 hover:bg-rose-50 text-gray-400 hover:text-rose-600 flex items-center justify-center transition-colors border border-gray-100 hover:border-rose-200 cursor-pointer"
                        title="Delete Category"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {c.description && (
                    <p className="text-xs text-gray-500 mt-3 line-clamp-2">{c.description}</p>
                  )}
                </div>

                {/* Tags list (For Company Categories) */}
                {catalogTypeTab === 'COMPANY' && (
                  <div className="mt-5 pt-4 border-t border-gray-100">
                    <h4 className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-2.5 select-none">Filter Tags</h4>
                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1">
                      {c.tags && c.tags.length > 0 ? (
                        c.tags.map(t => (
                          <span
                            key={t.id}
                            className={`px-2.5 py-1 text-[9px] font-bold rounded-lg border transition-all select-none flex items-center gap-1 ${t.isActive
                              ? 'bg-teal-50/60 text-teal-700 border-teal-100/50'
                              : 'bg-red-50/60 text-red-500 border-red-100/50 line-through'
                              }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${t.isActive ? 'bg-teal-500' : 'bg-red-500'}`}></span>
                            {t.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-[10px] text-gray-400 italic">No tags associated.</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-505 font-medium border-t border-gray-100 pt-5 mt-6 select-none bg-gray-50/40">
          <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
            {filteredCategories.length === 0 ? (
              'No records available'
            ) : (
              `Showing ${safeCategoryPage * categoryPageSize + 1} to ${Math.min((safeCategoryPage + 1) * categoryPageSize, filteredCategories.length)} of ${filteredCategories.length} entries`
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
            <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
              <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Show</span>
              <div className="relative">
                <select
                  value={categoryPageSize}
                  onChange={(e) => {
                    setCategoryPageSize(Number(e.target.value));
                    setCategoryPage(0);
                  }}
                  className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                </select>
                <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                  <ChevronDown size={12} />
                </span>
              </div>
              <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Entries</span>
            </div>

            {totalCategoryPages > 0 && (
              <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setCategoryPage(p => Math.max(0, p - 1))}
                  disabled={safeCategoryPage === 0}
                  className="bg-white border border-gray-200 text-gray-750 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                >
                  Previous
                </button>

                <div className="hidden sm:flex items-center gap-1">
                  {getPageNumbers(safeCategoryPage, totalCategoryPages).map((pageNum, idx) => {
                    if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                      return (
                        <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                          ...
                        </span>
                      );
                    }
                    const isActive = safeCategoryPage === pageNum;
                    return (
                      <button
                        key={`page-${pageNum}`}
                        type="button"
                        onClick={() => setCategoryPage(pageNum)}
                        className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                          ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                </div>

                <span className="sm:hidden text-xs text-gray-550 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-xs min-w-[70px] text-center">
                  {safeCategoryPage + 1} / {totalCategoryPages}
                </span>

                <button
                  type="button"
                  onClick={() => setCategoryPage(p => Math.min(totalCategoryPages - 1, p + 1))}
                  disabled={safeCategoryPage >= totalCategoryPages - 1}
                  className="bg-white border border-gray-250 text-gray-750 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Category Modal */}
      {showCreateCategoryModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 select-none">
              <h3 className="text-lg font-bold text-gray-800">
                Create {catalogTypeTab === 'COMPANY' ? 'Company Category' : 'Individual Business Category'}
              </h3>
              <button onClick={() => setShowCreateCategoryModal(false)} className="text-gray-400 hover:text-gray-655 cursor-pointer">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateCategorySubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Category Name</label>
                <input
                  type="text"
                  required
                  placeholder={catalogTypeTab === 'COMPANY' ? "e.g. Modular Kitchens" : "e.g. Carpenter, Electrician, Plumber"}
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-800"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Description (Optional)</label>
                <input
                  type="text"
                  placeholder="Brief summary or scope"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-800"
                />
              </div>

              {catalogTypeTab === 'COMPANY' && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Category Brands (comma-separated)</label>
                    <input
                      type="text"
                      placeholder="e.g. Sleek, Hafele, Hettich"
                      value={categoryForm.brands}
                      onChange={(e) => setCategoryForm({ ...categoryForm, brands: e.target.value })}
                      className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-800"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 select-none">Separate individual brands with a comma.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Search Tags (comma-separated)</label>
                    <input
                      type="text"
                      placeholder="e.g. PVC Cabinets, Chimneys, L-Shape Layout"
                      value={categoryForm.tags}
                      onChange={(e) => setCategoryForm({ ...categoryForm, tags: e.target.value })}
                      className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-800"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 select-none">Separate individual filter tags with a comma.</p>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 mt-6 select-none">
                <button
                  type="button"
                  onClick={() => setShowCreateCategoryModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-605 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {selectedCategory && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 select-none">
              <h3 className="text-lg font-bold text-gray-800">Edit Category: {selectedCategory.name}</h3>
              <button onClick={() => setSelectedCategory(null)} className="text-gray-400 hover:text-gray-655 cursor-pointer">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleEditCategorySubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Category Name</label>
                <input
                  type="text"
                  required
                  value={selectedCategory.name}
                  onChange={(e) => setSelectedCategory({ ...selectedCategory, name: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-850"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Description</label>
                <input
                  type="text"
                  value={selectedCategory.description}
                  onChange={(e) => setSelectedCategory({ ...selectedCategory, description: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-850"
                />
              </div>

              {(selectedCategory.categoryType || selectedCategory.category_type || catalogTypeTab || 'COMPANY').toUpperCase() === 'COMPANY' && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Category Brands (comma-separated)</label>
                    <input
                      type="text"
                      value={selectedCategory.brandsString}
                      onChange={(e) => setSelectedCategory({ ...selectedCategory, brandsString: e.target.value })}
                      className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-850"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 select-none">Add, delete, or rename brands by separating them with commas.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Search Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={selectedCategory.tagsString}
                      onChange={(e) => setSelectedCategory({ ...selectedCategory, tagsString: e.target.value })}
                      className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-855"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 select-none">Add, delete, or rename tags by separating them with commas.</p>
                  </div>
                </>
              )}

              <div className="flex items-center gap-2 py-1.5 select-none">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={selectedCategory.isActive}
                  onChange={(e) => setSelectedCategory({ ...selectedCategory, isActive: e.target.checked })}
                  className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                />
                <label htmlFor="isActive" className="text-xs font-bold text-gray-600 uppercase cursor-pointer">Category is Active</label>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 mt-6 select-none">
                <button
                  type="button"
                  onClick={() => setSelectedCategory(null)}
                  className="px-4 py-2 border border-gray-200 text-gray-605 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Category Warning & Double Verification Modal */}
      {deleteTargetCategory && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-red-100 animate-in fade-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="p-5 border-b border-rose-100 bg-rose-50/60 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-rose-900">Confirm Category Deletion</h3>
                <p className="text-xs text-rose-700 font-medium">Double-verification safety check</p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {deleteConfirmStep === 1 ? (
                /* Step 1: Warning Message */
                <>
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs leading-relaxed space-y-2">
                    <div className="flex items-center gap-2 font-bold text-amber-950">
                      <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      Warning: Irreversible Action
                    </div>
                    <p>
                      You are about to delete category <strong className="text-amber-950">"{deleteTargetCategory.name}"</strong>.
                    </p>
                    <p className="text-[11px] text-amber-800">
                      Deleting this category will permanently remove it from catalog selections. If active listings are associated with this category, the deletion will be blocked by system safety rules.
                    </p>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setDeleteTargetCategory(null)}
                      className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmStep(2)}
                      className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold shadow-md shadow-rose-600/20 cursor-pointer"
                    >
                      Proceed to Final Step
                    </button>
                  </div>
                </>
              ) : (
                /* Step 2: Final Verification Input */
                <>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    To confirm deletion, please type the category name <strong className="text-gray-900">"{deleteTargetCategory.name}"</strong> below:
                  </p>

                  <input
                    type="text"
                    value={confirmCategoryNameInput}
                    onChange={(e) => setConfirmCategoryNameInput(e.target.value)}
                    placeholder={deleteTargetCategory.name}
                    className="w-full bg-white border border-gray-300 rounded-lg p-2.5 text-xs font-bold text-gray-850 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 focus:outline-none"
                  />

                  <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteConfirmStep(1);
                        setConfirmCategoryNameInput('');
                      }}
                      className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 cursor-pointer"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      disabled={confirmCategoryNameInput.trim() !== deleteTargetCategory.name.trim() || deletingCategory}
                      onClick={handleDeleteCategoryConfirmed}
                      className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold shadow-md shadow-rose-600/20 cursor-pointer transition-all flex items-center gap-1.5"
                    >
                      {deletingCategory ? 'Deleting...' : 'Permanently Delete Category'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesTagsTab;
